import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Palette, Save, Check, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const colorPresets = [
  {
    name: 'Nero & Oro',
    id: 'dark-gold',
    preview: { bg: '#0f0f0f', card: '#1a1a1a', accent: '#d4a843', text: '#fafafa' },
    css: {
      '--background': '0 0% 6%',
      '--foreground': '0 0% 98%',
      '--card': '0 0% 10%',
      '--card-foreground': '0 0% 98%',
      '--secondary': '0 0% 14%',
      '--secondary-foreground': '0 0% 98%',
      '--accent': '41 58% 55%',
      '--accent-foreground': '0 0% 6%',
      '--muted': '0 0% 20%',
      '--muted-foreground': '0 0% 64%',
      '--border': '0 0% 18%',
    }
  },
  {
    name: 'Nero & Argento',
    id: 'dark-silver',
    preview: { bg: '#0f0f0f', card: '#1a1a1a', accent: '#a0a0a0', text: '#fafafa' },
    css: {
      '--background': '0 0% 6%',
      '--foreground': '0 0% 98%',
      '--card': '0 0% 10%',
      '--card-foreground': '0 0% 98%',
      '--secondary': '0 0% 14%',
      '--secondary-foreground': '0 0% 98%',
      '--accent': '0 0% 63%',
      '--accent-foreground': '0 0% 6%',
      '--muted': '0 0% 20%',
      '--muted-foreground': '0 0% 55%',
      '--border': '0 0% 18%',
    }
  },
  {
    name: 'Blu Notte & Oro',
    id: 'navy-gold',
    preview: { bg: '#0a1628', card: '#0f1f3a', accent: '#d4a843', text: '#e8ecf4' },
    css: {
      '--background': '220 55% 10%',
      '--foreground': '220 20% 93%',
      '--card': '220 45% 15%',
      '--card-foreground': '220 20% 93%',
      '--secondary': '220 40% 20%',
      '--secondary-foreground': '220 20% 93%',
      '--accent': '41 58% 55%',
      '--accent-foreground': '220 55% 10%',
      '--muted': '220 30% 25%',
      '--muted-foreground': '220 15% 60%',
      '--border': '220 35% 22%',
    }
  },
  {
    name: 'Verde Scuro & Oro',
    id: 'dark-green-gold',
    preview: { bg: '#0a1a0f', card: '#112218', accent: '#c9a84c', text: '#e8f0ea' },
    css: {
      '--background': '140 35% 7%',
      '--foreground': '140 15% 93%',
      '--card': '140 30% 11%',
      '--card-foreground': '140 15% 93%',
      '--secondary': '140 25% 15%',
      '--secondary-foreground': '140 15% 93%',
      '--accent': '45 50% 54%',
      '--accent-foreground': '140 35% 7%',
      '--muted': '140 20% 20%',
      '--muted-foreground': '140 10% 58%',
      '--border': '140 25% 17%',
    }
  },
];

const AdminDesignSection = () => {
  const [selectedPreset, setSelectedPreset] = useState('dark-gold');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders } = useAuth();

  useEffect(() => { fetchCurrentTheme(); }, []);

  const fetchCurrentTheme = async () => {
    try {
      const response = await axios.get(`${API}/site-texts`);
      if (response.data?.theme) {
        setSelectedPreset(response.data.theme);
      }
    } catch (error) { console.error('Failed to fetch theme:', error); }
    setLoading(false);
  };

  const applyTheme = (presetId) => {
    const preset = colorPresets.find(p => p.id === presetId);
    if (!preset) return;
    const root = document.documentElement;
    Object.entries(preset.css).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  };

  const handlePreview = (presetId) => {
    setSelectedPreset(presetId);
    applyTheme(presetId);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-texts`, { theme: selectedPreset }, { headers: getAuthHeaders() });
      toast.success('Tema salvato!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
    setSaving(false);
  };

  useEffect(() => {
    if (!loading && selectedPreset) {
      applyTheme(selectedPreset);
    }
  }, [loading]);

  if (loading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="design-section">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold mb-2">Design & Colori</h2>
        <p className="text-muted-foreground text-sm">Personalizza l'aspetto del tuo sito. L'anteprima si applica subito.</p>
      </div>

      <Card className="card-elegant">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Palette className="h-5 w-5 text-accent" /> Tema Colori
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Label>Seleziona un tema</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {colorPresets.map((preset) => (
              <button
                key={preset.id}
                onClick={() => handlePreview(preset.id)}
                className={`p-4 rounded-xl border-2 text-left transition-all group ${
                  selectedPreset === preset.id ? 'border-accent shadow-lg shadow-accent/10' : 'border-border hover:border-foreground/20'
                }`}
                data-testid={`color-preset-${preset.id}`}
              >
                {/* Preview */}
                <div
                  className="rounded-lg p-3 mb-3 border border-white/10"
                  style={{ backgroundColor: preset.preview.bg }}
                >
                  <div className="flex gap-2 mb-2">
                    <div className="h-2 rounded-full flex-1" style={{ backgroundColor: preset.preview.accent }} />
                    <div className="h-2 rounded-full w-8" style={{ backgroundColor: preset.preview.card }} />
                  </div>
                  <div
                    className="rounded p-2"
                    style={{ backgroundColor: preset.preview.card }}
                  >
                    <div className="h-1.5 rounded-full w-3/4 mb-1" style={{ backgroundColor: preset.preview.text, opacity: 0.7 }} />
                    <div className="h-1.5 rounded-full w-1/2" style={{ backgroundColor: preset.preview.text, opacity: 0.4 }} />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-1">
                      <div className="w-5 h-5 rounded-full border-2 border-background" style={{ backgroundColor: preset.preview.bg }} />
                      <div className="w-5 h-5 rounded-full border-2 border-background" style={{ backgroundColor: preset.preview.accent }} />
                      <div className="w-5 h-5 rounded-full border-2 border-background" style={{ backgroundColor: preset.preview.card }} />
                    </div>
                    <p className="text-sm font-semibold">{preset.name}</p>
                  </div>
                  {selectedPreset === preset.id && (
                    <div className="w-5 h-5 rounded-full bg-accent flex items-center justify-center">
                      <Check className="h-3 w-3 text-accent-foreground" />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="save-design-btn">
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio...</> : <><Save className="mr-2 h-4 w-4" /> Salva Tema</>}
        </Button>
      </div>
    </div>
  );
};

export default AdminDesignSection;
